import { useEffect, useState } from 'react';

import { useLoaderData } from '@remix-run/react';
import type { SerializableNode } from '@shared/dream-components';

import useProprietaryFooter from '~/hooks/useProprietaryFooter';
import type { Page } from '~/interfaces/page';
import type { Site } from '~/interfaces/site';
import type { PostLoaderData } from '~/routes/p/$slug';
import type { StaticPageLoaderData } from '~/services/static_page';

import { PostEditorCustomPageContent } from './nodes/PostEditorCustomPageContent';
import { ProprietaryFooter } from './nodes/ProprietaryFooter';
import { serializeNode } from './nodes/serializeNode';

// TODO: Comment back in when this is ready to go live
// type PostInsights = {
//   [postId: string]: {
//     likes: number;
//     comments: number;
//   };
// };

interface PageSerializerProps {
  site: Site;
  page: Page;
  isFooterVisible: boolean;
  // TODO: Comment back in when this is ready to go live
  // postInsights: PostInsights;
}

const PageSerializer = ({ page, site, isFooterVisible }: PageSerializerProps) => {
  const loaderData = useLoaderData<StaticPageLoaderData | PostLoaderData>();

  // Handle both StaticPageLoaderData and PostLoaderData
  const isContentClipped = loaderData.isContentClipped || false;
  const clippedPageContent = 'clippedPageContent' in loaderData ? loaderData.clippedPageContent : null;

  const pageData = isContentClipped ? (clippedPageContent as SerializableNode) : page.viewable_page_version.content;
  const isPostPage = page?.name === 'Post';

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }

    return () => {
      setMounted(false);
    };
  }, []);

  const { isPageContentOverflowing, textColor, backgroundColor } = useProprietaryFooter(
    pageData as any,
    site.publication.web_theme,
  );

  if (!pageData) {
    return null;
  }

  let background = backgroundColor ?? 'transparent';

  // Trying to get the background color from the last section
  try {
    if (pageData?.content?.length) {
      const lastSection = pageData.content.at(-1);
      if (lastSection?.attrs?.backgroundColor) {
        background = lastSection.attrs.backgroundColor;
      }
    }
  } catch {
    background = backgroundColor ?? 'transparent';
  }

  // Check if this page uses the post editor
  const usesPostEditor = page.uses_post_editor || false;

  return (
    <>
      {usesPostEditor ? (
        <PostEditorCustomPageContent content={pageData as any} />
      ) : (
        serializeNode(pageData, isPostPage ? 'rendered-post' : '')
      )}
      {!isFooterVisible && mounted ? (
        <ProprietaryFooter
          node={{
            type: 'proprietaryFooter',
            attrs: {
              id: 'proprietaryFooter',
              backgroundColor: background,
              color: textColor,
            },
          }}
          showPoweredByBeehiiv={false}
          shouldRenderOverContent={!isPageContentOverflowing}
        />
      ) : null}
    </>
  );
};

export default PageSerializer;
